import { Component, OnInit, inject } from '@angular/core'
import { catchError, finalize, of, tap } from 'rxjs'
import { BaseComponent } from '../../../common/components/base.component'
import { AppStateService } from '../../../common/store/state/app/app.state.service'
import { AuthService } from '../../../common/services/auth.service'
import { SsoAuthService, SSO_PENDING_KEY } from '../../../common/services/sso-auth.service'
import { LoginFlowStateService } from '../../../common/store/state/login-flow/login-flow.state.service'
import { LaunchAuthRedirectService } from '../../../common/services/launch-auth-redirect.service'
import { getPendingLaunchId } from '../../../common/util/session-util'

@Component({
  selector: 'app-sso-callback',
  standalone: false,
  template: `<div class="flex justify-content-center mt-5 text-xl">Completing sign in&hellip;</div>`
})
export class SsoCallbackComponent extends BaseComponent implements OnInit {

  #ssoAuthService = inject(SsoAuthService)
  #authService = inject(AuthService)
  #appStateService = inject(AppStateService)
  #loginFlowStateService = inject(LoginFlowStateService)
  launchAuthRedirectService = inject(LaunchAuthRedirectService)

  ngOnInit(): void {
    this.launchAuthRedirectService.captureFromQueryParams(this.route.snapshot.queryParamMap)
    this.spinnerService.setLoading(true, 'login')
    this.#complete()
  }

  async #complete(): Promise<void> {
    let accessToken: string
    try {
      const result = await this.#ssoAuthService.handleRedirect()
      if (!result) {
        this.#finish()
        this.router.navigate(['/login'])
        return
      }
      accessToken = result.accessToken
    } catch (error) {
      console.warn('SSO redirect could not be completed', error)
      this.#finish()
      this.notificationService.showError('Unable to complete sign in.')
      this.router.navigate(['/login'])
      return
    }

    const pending = this.#readPending()
    const email = pending?.email ?? ''
    const byPassLocation = pending?.byPassLocation ?? false
    this.launchAuthRedirectService.setPendingLaunchId(pending?.launchId)

    // Device is captured before the redirect
    if (!this.#appStateService.getDevice()) {
      await this.#authService.initDevice(byPassLocation)
    }

    this.subscriptions.add(this.#authService.ssoLogin(accessToken, email, byPassLocation)
      .pipe(tap(() => {
        this.#navigate()
      }), catchError((httpErrorResp) => {
        console.log(httpErrorResp)
        this.notificationService.showError(httpErrorResp.error ? httpErrorResp.error : 'Invalid Login Credentials.')
        this.router.navigate(['/login'])
        return of(httpErrorResp)
      }), finalize(() => {
        this.#finish()
      })).subscribe())
  }

  #finish(): void {
    sessionStorage.removeItem(SSO_PENDING_KEY)
    this.spinnerService.setLoading(false, 'login')
  }

  #readPending(): { email: string; byPassLocation: boolean; launchId?: string | null } | null {
    const raw = sessionStorage.getItem(SSO_PENDING_KEY)
    if (!raw) {
      return null
    }
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }

  // Mirrors LoginComponent's post-login routing so SSO users land in the same place.
  async #navigate() {
    const loginFlow = this.#loginFlowStateService.getLoginFlow()
    if (loginFlow.Do2FaSetup) {
      return this.router.navigate(['/mfa/collection/email'])
    } else if (loginFlow.Do2FaCheck) {
      return this.router.navigate(['/mfa/selection'])
    } else if (loginFlow.DoSecurityQandA) {
      return this.router.navigate(['/security-qa'])
    } else if (this.featureFlagService.isLICProcessEnabled()) {
      const launchId = getPendingLaunchId();
      if (launchId) {
        return this.router.navigate(['auth/lic'], { queryParams: { ignoreLaunchContext: true } })
      }
    }

    return this.router.navigate([this.#authService.getHomeRoute()])
  }
}
